import type { LatestRiskScoreByRiskIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetLatestRiskScoresByRiskIdSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLatestRiskScoresByRiskIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

/**
 * The `risk_score` row is upserted asynchronously by the V3 risk-score event
 * consumer (LINKED_ITEM_CREATED → recalc → POST /risk-scores/batch), so the
 * rater's own invalidation races it and other users' updates can't be
 * observed client-side — the V3 stack has no subscription transport, so the
 * feed is POLLED. 5s matches the change-request panel polls
 * (`useGetChangeRequestsByParentSubscription`).
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Holding-pattern subscription hook for a risk's `risk_score` aggregate row
 * (RSP-5879). Backs `useRiskScore` — the risk overview side panel's live
 * Inherent/Residual ratings.
 *
 * - `trpc` ON  → polls `frontend.risk.latestRiskScoresByRiskId` and maps the
 *   row into the legacy subscription shape (`{ risk_score: [...] }`).
 * - `trpc` OFF → the legacy Hasura `getLatestRiskScoresByRiskId` subscription.
 *
 * Built on `createSubscriptionHook` so the poll → real tRPC subscription swap
 * (RSP-4320) is a one-place change. Replaces the raw `useSubscription` that
 * previously lived inline in `useRiskScore`.
 */
export const useGetLatestRiskScoresByRiskId = createSubscriptionHook<
  { riskId: string },
  LatestRiskScoreByRiskIdResponse | null,
  GetLatestRiskScoresByRiskIdSubscription
>({
  trpcQueryOptions: (trpc, { riskId }) =>
    trpc.frontend.risk.latestRiskScoresByRiskId.queryOptions({ riskId }),
  trpcRefetchInterval: () => POLL_INTERVAL_MS,
  mapTrpcDataToGraphQL: (row) => ({
    risk_score: row?.riskScore ? [row.riskScore] : [],
  }),
  graphqlDocument: GetLatestRiskScoresByRiskIdDocument,
  graphqlVariables: ({ riskId }) => ({ RiskId: riskId }),
});
